<template>
  <div v-if="isReady" class="content" data-test="bio-material-view">
    <h2 v-if="!hideTitle">
      {{ materialTypeLabel }}
    </h2>
    <template v-for="displayField in getPopulatedDisplayFields">
      <BioPlasmid
        v-if="displayField.name === 'field_biomat_plasmid_vector_type'"
        :key="displayField.name"
      >
        <FieldDisplay
          v-for="(plasmidField, index) in plasmidDisplay"
          :key="plasmidField ? plasmidField.name : 'plasmid-field-' + index"
          :display-field="plasmidField"
          :hide-empty="true"
          :individual-field-data="
            plasmidFields && plasmidField
              ? plasmidFields[plasmidField.name]
              : {}
          "
        />
      </BioPlasmid>
      <FieldDisplay
        v-else
        :key="displayField?.name"
        :display-field="displayField"
        :individual-field-data="fieldData?.[displayField?.name]"
        :show-library-style="showLibraryStyle"
      />
    </template>
    <div class="content-buttons">
      <button
        class="secondary"
        data-test="addtolibrary"
        v-if="canAddToLibrary"
        @click="addToLibrary"
      >
        Add to Library
      </button>
      <b-button
        v-if="showAddToLibrary"
        data-test="goBack"
        type="button"
        @click.prevent="goBack"
        variant="primary"
      >
        Go Back
      </b-button>
    </div>
  </div>
</template>

<script>
import FieldDisplay from "./FieldDisplay.vue";
import BioPlasmid from "./BioPlasmid.vue";
import EntityFormHelper from "../../lib/EntityFormHelper";
import EntityViewDisplayFieldFormatter from "../../lib/entity_view_display_field_formatters";
import raftGenericHelper from "../../lib/raft_generic_helper";
import { restClient, jsonClient } from "../../lib/raftApi";

export default {
  name: "BioMaterialView",
  inject: ["isB4"],
  components: { FieldDisplay, BioPlasmid },
  props: {
    materialType: {
      type: String,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    groupNid: {
      type: String,
      default: null,
    },
    omitFields: {
      type: Array,
      default() {
        return [];
      },
    },
    showAddToLibrary: {
      type: Boolean,
      default: true,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    bioMaterialData: {
      type: Object,
      default: null,
    },
    showLibraryStyle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultQueries: {
        include: "og_audience,parent_reference",
        "fields[node--laboratory]": "title,drupal_internal__nid",
        "fields[node--department]": "title,drupal_internal__nid",
        "fields[node--raft_unit]": "title,drupal_internal__nid",
      },
      materialTypeLabel: "",
      response: null,
      displayData: null,
      fieldData: null,
      perms: [],
      plasmidFields: {},
      plasmidDisplay: {},
    };
  },
  computed: {
    canAddToLibrary: function () {
      const perm = "promote bio_material entities";
      const active = this.response?.data?.attributes?.operative_status;
      return (
        this.showAddToLibrary &&
        active &&
        !this.inLibrary &&
        this.perms?.includes(perm)
      );
    },
    getPopulatedDisplayFields: function () {
      return this.getDisplayFields?.filter((displayField) =>
        this.isPopulated(this.fieldData[displayField.name]),
      );
    },
    getDisplayFields: function () {
      return this.buildDisplayFields(
        this.fieldData,
        this.displayData,
        this.omitFields,
      );
    },
    hasPlasmid() {
      return (
        this.response?.included?.findIndex(
          (includedItem) => includedItem.type === "bio_material--plasmid",
        ) > -1
      );
    },
    inLibrary() {
      return this.response?.data?.attributes?.in_library === true;
    },
    isReady: function () {
      return (
        this.isNotEmpty(this.displayData) && this.isNotEmpty(this.response)
      );
    },
  },
  async mounted() {
    const path = "/entity-view-display/bio_material/" + this.materialType;
    const queries = {
      display_id: "default",
    };

    const restResponseData = await restClient
      .get({ path, queries })
      .catch(({ message, data }) => {
        this.errorEmit(message, data);
      });
    const restData = restResponseData?.[this.materialType];
    this.displayData = restData?.default;
    this.materialTypeLabel = restData?.["bundle-label"];

    if (this.groupNid) {
      this.defaultQueries["filter[og_audience]"] = this.groupNid;
      this.defaultQueries["rog-id"] = this.groupNid;
      this.defaultQueries["rog-type"] = "node";
    }
    if (this.displayData?.field_biomat_plasmid_vector_type) {
      this.defaultQueries.include += ",field_biomat_plasmid_vector_type";
    }
    let jsonApiResponseData = null;
    // A parent component may provided the needed data.
    if (this.bioMaterialData) {
      jsonApiResponseData = this.bioMaterialData;
    } else {
      const jsonApiPath =
        "/bio_material/" + this.materialType + "/" + this.uuid;
      jsonApiResponseData = await jsonClient
        .get({ path: jsonApiPath, queries: this.defaultQueries })
        .catch(({ message, data }) => {
          this.errorEmit(message, data);
        });
    }

    this.fieldData = this.buildFieldData(
      jsonApiResponseData,
      jsonApiResponseData?.data?.attributes,
      jsonApiResponseData?.deserialized,
      ["og_audience", "field_biomat_plasmid_vector_type", "parent_reference"],
    );
    this.response = jsonApiResponseData;
    if (this.hasPlasmid) {
      this.getPlasmidFieldsAndDisplay();
    }
    if (this.showAddToLibrary) {
      this.fetchUserPermissions();
    }
  },
  methods: {
    fetchUserPermissions() {
      const permissionList = [
        "edit bio_material entities",
        "promote bio_material entities",
      ];
      this.perms = raftGenericHelper.fetchUserPermissions(
        permissionList,
        this.groupNid,
      );
    },
    getPlasmidFieldsAndDisplay: async function () {
      const plasmidFieldResponse = this.response?.included?.find(
        (includedItem) => includedItem.type === "bio_material--plasmid",
      );
      this.plasmidFields = this.buildFieldData(
        plasmidFieldResponse,
        plasmidFieldResponse?.attributes,
        plasmidFieldResponse?.deserialized,
      );
      const plasmidDisplayResponseData = await restClient
        .get({
          path: "/entity-view-display/bio_material/plasmid",
          queries: { display_id: "default" },
        })
        .catch(({ message, data }) => {
          this.errorEmit(message, data);
        });
      // Omit showing the group fields if the plasmid is in the Library.
      let omit = [];
      // @todo RAFT-6058 handle this with field access or view display.
      if (plasmidFieldResponse.attributes.in_library) {
        omit = ["research_gene_mod_category", "research_genetic_modification"];
      }
      this.plasmidDisplay = this.buildDisplayFields(
        this.plasmidFields,
        plasmidDisplayResponseData?.plasmid?.default,
        omit,
      );
    },
    isPopulated: function (obj) {
      // Handles object and array.
      if (obj && typeof obj === "object") {
        return Object.keys(obj).length > 0;
      }
      if (typeof obj === "boolean" || typeof obj === "number") {
        return true;
      }
      return !!obj;
    },
    isNotEmpty: function (obj) {
      // Handles object and array.
      if (obj && typeof obj === "object") {
        return Object.keys(obj).length > 0;
      }
      return !!obj;
    },
    buildDisplayFields(fieldData, displayData, omit) {
      const displayFields = [];
      Object.entries(displayData).forEach((display) => {
        const [fieldName, data] = display;
        if (!omit.includes(fieldName)) {
          const field = { name: fieldName, label: data.label };
          field.formatted = EntityViewDisplayFieldFormatter.fieldFormatter(
            fieldData[fieldName],
            data,
          );
          displayFields.push(field);
        }
      });
      return displayFields;
    },
    buildFieldData(response, attributes, deserialized, relationships = null) {
      const tempFieldData = {};
      if (attributes) {
        Object.entries(attributes).forEach((field) => {
          const [name, value] = field;
          tempFieldData[name] = value;
        });
      }
      if (relationships) {
        relationships.forEach((key) => {
          if (deserialized?.hasOwnProperty(key)) {
            // @todo Unify with code in EntityViewDisplayFieldFormatters.formatEntityReferenceLabel().
            if (
              Array.isArray(deserialized[key]) &&
              deserialized?.[key].length &&
              deserialized?.[key][0].hasOwnProperty("name")
            ) {
              tempFieldData[key] = deserialized[key][0].name;
            } else if (deserialized?.[key]) {
              if (deserialized[key].hasOwnProperty("name")) {
                tempFieldData[key] = deserialized[key].name;
              }
              if (deserialized[key].hasOwnProperty("title")) {
                tempFieldData[key] = deserialized[key].title;
              }
            }
          }
        });
      }
      return tempFieldData;
    },
    addToLibrary: async function () {
      const drupalInternalId =
        this.response?.data?.attributes?.drupal_internal__id;
      if (drupalInternalId) {
        const path = `/bio-materials/group-to-library/${drupalInternalId}`;
        const postResponseData = await restClient
          .post({ path, queries: {}, data: {} })
          .catch(({ message, data }) => {
            this.errorEmit(message, data);
          });
        if (postResponseData.uuid) {
          const url = `/raft/biologicals/${this.materialType}/${
            postResponseData?.uuid?.[0]?.value
          }?${raftGenericHelper.getDestinationQuery()}`;
          window.location.href = url;
        }
      }
    },
    goBack() {
      EntityFormHelper.gotoDestination();
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
  },
};
</script>

<style scoped>
.content-buttons {
  margin: 1rem 0;
}
</style>
